import { memo } from 'react'

import { ProductV2, formatPriceText } from '@common'
import cx from 'classnames'
import Image from 'next/image'

import EventBackground from '@/assets/images/storefronts/clashofclans/passcard-event-bg.png'
import SeasonBackground from '@/assets/images/storefronts/clashofclans/passcard-gold-bg.png'
import BgFlare from '@/assets/images/storefronts/clashofclans/shop_item_bg_flare.png'
import CRPassBackground from '@/assets/images/storefronts/clashroyale/passcard-bg.png'
import { useLocale } from '@/features/locale/useLocale'
import { ProductRibbon } from '@/features/storefronts/_storeBase/cardRibbon/ProductRibbon'
import { StampCard } from '@/features/storefronts/_storeBase/stampCard/StampCard'
import { useTranslatable } from '@/localization/useTranslatable'
import { resolveProductImage } from '@/utils/resolveProductImage'
import { useAppSelector } from '@/utils/useAppSelector'

import { useSnowplowProductCardViewImpression } from '../../../../utils/snowplow'
import { useGameSlug } from '../../game-provider'
import { BuyButton } from '../BuyButton/BuyButton'
import { CardBase } from '../card/CardBase'
import { useHandleClick } from '../card/useHandleClick'
import { StampCardRewardInfo } from '../stampCard/StampCardRewardInfo'

import styles from './PassCard.module.scss'

interface PassCardProps {
  product: ProductV2
  productPage?: boolean
}

export const PassCard: React.FC<PassCardProps> = memo(({ product, productPage = false }) => {
  const translatable = useTranslatable()
  const locale = useLocale()
  const market = useAppSelector((state) => state.config.market)
  const gameSlug = useGameSlug()
  const pathname = `${gameSlug}/product/${product.slug}`
  const { handleClick } = useHandleClick({ product, pathname })

  const currency = market?.currencyCode
  const currencyCountry = market?.currencyCountry
  const price = currencyCountry && formatPriceText(product.prices[currencyCountry] ?? 0, locale, currency)

  const isPass = !!product.contentCounts.SEASON_PASS
  const isSeasonPass = isPass && product.type === 'product'
  const isEventPass = isPass && product.type === 'offer'
  const hasStampCard = isSeasonPass && product.stampCardBalance

  const isClashOfClans = gameSlug === 'clashofclans'
  const isClashRoyale = gameSlug === 'clashroyale'

  const HeaderImage = () => {
    switch (gameSlug) {
      case 'clashofclans':
        return isEventPass ? EventBackground : SeasonBackground
      case 'clashroyale':
        return CRPassBackground
      default:
        return ''
    }
  }

  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    product,
    'PassCard',
    'PassCard',
  )

  const isPassCard = isSeasonPass || isEventPass

  if (!isPassCard) {
    return null
  }

  return (
    <CardBase
      href={pathname}
      className={cx(styles.PassCard, styles[gameSlug], { [styles.productPage]: productPage })}
      onClick={handleClick}
    >
      <div className={styles.Header} ref={snowplowViewImpressionRef}>
        <Image
          className={styles.HeaderImage}
          src={HeaderImage()}
          alt="passcard-background"
          fill
          sizes="(max-width: 600px) 400px, 600px"
          quality={100}
          style={{ objectFit: 'cover' }}
        />
        {isClashOfClans && (
          <div className={styles.bgFlare}>
            <Image src={BgFlare} alt="product flare" fill sizes="200px" />
          </div>
        )}
        <div className={styles.LogoContainer}>
          <Image
            className={styles.Logo}
            src={resolveProductImage(product)}
            fill
            sizes="(max-width: 500px) 300px, 400px"
            alt="passcard icon"
            placeholder="empty"
          />
        </div>
        <ProductRibbon style="darkBlue" product={product} position="s" />
        {isClashOfClans && (
          <div className={styles.Title}>
            <span>{translatable(product.title, 'title')}</span>
          </div>
        )}
        {isClashRoyale && (
          <div className={styles.Title}>
            <span>{translatable(product.title, 'title')}</span>
          </div>
        )}

        {hasStampCard && (
          <div className={styles.StampCard}>
            <StampCard product={product} size="medium" />
          </div>
        )}
      </div>

      <div className={styles.Footer}>
        <div className={styles.buttonWrapper}>
          <BuyButton
            additionalClasses={styles.Button}
            type="Primary"
            content={price}
            product={product}
            textScaling="Small"
          />
        </div>

        {hasStampCard && <StampCardRewardInfo game={gameSlug} />}
      </div>
    </CardBase>
  )
})

PassCard.displayName = 'PassCard'
